/**
 * Academic calendar — AY 2026-27.
 *
 * Transcribed from the institute's own circular: "Tentative Academic Calendar
 * for Autonomous 3rd and 5th Semester, Academic Year 2026–2027" (Ref. No.
 * BIET/ACA/AUTO/009/2026-27, dated 13-08-2026, signed by the Dean Academics,
 * Director and Principal). That circular covers only the odd semester for
 * III and V sem — it states no working-day count, and carries no holiday
 * list or working-Saturday schedule, so those are not claimed here either.
 * The circular's own two columns (III Sem / V Sem) print identical dates for
 * every row, reproduced below as two terms rather than merged into one, so
 * the "semesters" count on the page stays accurate to what was published.
 *
 * Single page surface at /academics/calendar.
 */

export interface CalendarEvent {
  date: string;
  title: string;
  /** Lightweight tag — shown as a chip. */
  kind: "term" | "exam" | "holiday" | "result" | "ceremonial";
}

export interface AcademicTerm {
  name: string;
  /** Short label — "Sem III", "Sem V", etc. */
  short: string;
  span: string;
  /** Not stated on every circular — omit rather than invent a figure. */
  workingDays?: number;
  events: CalendarEvent[];
}

export const ACADEMIC_YEAR = "2026-27";
export const PUBLISHED_ON = "Aug 13, 2026";

/** The circular itself, ingested via scripts/ingest-docs.py. */
export const CALENDAR_PDF = {
  url: "sites/biet/docs/academics/tentative-academic-calendar-for-autonomous-3rd-5th-semester-academic-year-2026-27.pdf",
  name: "Tentative Academic Calendar — III & V Semester, AY 2026-27",
  bytes: 626304,
};

/**
 * III Sem and V Sem run identical dates per the circular — one list, two
 * terms. Exported so /examinations can build its own "Important dates" view
 * from the same source instead of carrying a second, divergent copy.
 */
export const SEM_III_V_EVENTS: CalendarEvent[] = [
  { date: "Sep 15 — 16", title: "Registration of courses", kind: "term" },
  { date: "Sep 15", title: "Commencement of classes", kind: "term" },
  { date: "Sep 25", title: "Announcement of ABA", kind: "term" },
  { date: "Nov 04 — 06", title: "IA test 1 + Quiz 1 (portion: 2.5 modules)", kind: "exam" },
  { date: "Nov 05 — 09", title: "IA test 1 evaluation + ERP entry", kind: "exam" },
  { date: "Nov 11", title: "Last date for dropping of courses", kind: "term" },
  // Printed exactly as the circular states it — the range crosses back from
  // December to November, which reads as a likely error in the source, but
  // nothing here corrects it.
  { date: "Dec 11 — Nov 14", title: "Applying for SEE in ERP without fine", kind: "term" },
  { date: "Dec 16 — 18", title: "Applying for SEE in ERP with fine", kind: "term" },
  { date: "Dec 10", title: "ABA evaluation + ERP entry", kind: "term" },
  { date: "Dec 19", title: "Course exit survey and semester-end feedback in ERP", kind: "term" },
  { date: "Dec 21 — 23", title: "IA test 2 + Quiz 2 (portion: 2.5 modules)", kind: "exam" },
  { date: "Dec 24 — 28", title: "IA test 2 evaluation + ERP entry", kind: "exam" },
  { date: "Dec 29", title: "Last date for withdrawal of courses", kind: "term" },
  { date: "Before LWD −1 wk", title: "Lab IA test + ERP entry", kind: "exam" },
  { date: "Jan 04 — 06", title: "Finalization and freezing of CIE marks in ERP", kind: "result" },
  { date: "Jan 06", title: "Last working day of the semester", kind: "term" },
  { date: "Jan 11 — 25", title: "SEE: theory examinations", kind: "exam" },
  { date: "Jan 28 — Feb 10", title: "Practical examinations", kind: "exam" },
  { date: "Jan 28 — Feb 28", title: "Valuation, declaration of results, revaluation and RV results declaration, make-up examination", kind: "result" },
  { date: "Feb 11 — 21", title: "Semester vacation", kind: "holiday" },
  { date: "Feb 22", title: "Commencement of next semester", kind: "term" },
];

export const TERMS: AcademicTerm[] = [
  {
    name: "III Semester (Autonomous)",
    short: "Sem III",
    span: "Sep 15, 2026 — Jan 06, 2027",
    events: SEM_III_V_EVENTS,
  },
  {
    name: "V Semester (Autonomous)",
    short: "Sem V",
    span: "Sep 15, 2026 — Jan 06, 2027",
    events: SEM_III_V_EVENTS,
  },
];

/**
 * Not published on the III/V-sem circular this page now transcribes — no
 * holiday list or working-Saturday schedule is stated there. Left empty
 * rather than carrying the earlier, unsourced placeholder dates forward.
 */
export const HOLIDAYS_FULL: Array<{ date: string; title: string }> = [];
export const WORKING_SATURDAYS: string[] = [];
